import React from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LoginIcon from '@mui/icons-material/Login';
import HomeIcon from '@mui/icons-material/Home';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const GET_PUBLIC_APPOINTMENT_REGISTRATION = gql`
  query GetPublicAppointmentRegistration($appointmentId: ID!, $registrationToken: String!) {
    publicAppointmentRegistration(
      appointmentId: $appointmentId
      registrationToken: $registrationToken
    ) {
      id
      firstName
      lastName
      email
      phoneNumber
      subject
      description
      scheduledAt
      status
      notes
      cancellationReason
      department {
        id
        name
      }
      assignedTo {
        id
        firstName
        lastName
      }
      visit {
        id
        status
      }
    }
  }
`;

const getStatusLabel = (status) => {
  const labels = {
    PENDING: 'En attente',
    CONFIRMED: 'Confirmé',
    CANCELLED: 'Annulé',
    COMPLETED: 'Terminé',
  };
  return labels[status] || status;
};

const getStatusColor = (status) => {
  const colors = {
    PENDING: 'warning',
    CONFIRMED: 'success',
    CANCELLED: 'error',
    COMPLETED: 'info',
  };
  return colors[status] || 'default';
};

function PublicAppointmentRegistration() {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('registerVisit');
  const registrationToken = searchParams.get('token');

  const { data, loading, error } = useQuery(GET_PUBLIC_APPOINTMENT_REGISTRATION, {
    variables: {
      appointmentId,
      registrationToken,
    },
    skip: !appointmentId || !registrationToken,
    fetchPolicy: 'network-only',
  });

  const appointment = data?.publicAppointmentRegistration;
  const hostName = appointment?.assignedTo
    ? `${appointment.assignedTo.firstName || ''} ${appointment.assignedTo.lastName || ''}`.trim()
    : 'Non assigné';

  if (!appointmentId || !registrationToken) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">
          Le lien d&apos;enregistrement est incomplet. Vérifiez l&apos;URL reçue.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !appointment) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.graphQLErrors?.[0]?.message || error?.message || "Impossible de charger les informations du rendez-vous."}
        </Alert>
        <Button component={RouterLink} to="/" startIcon={<HomeIcon />}>
          Retour a l&apos;accueil
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, #ffffff 0%, #f5f9ff 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Box>
            <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.2 }}>
              Enregistrement de visite
            </Typography>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              Informations du rendez-vous
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Ce lien vous permet de consulter les informations associees a votre rendez-vous sans connexion.
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel(appointment.status)}
            color={getStatusColor(appointment.status)}
            sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
          />
        </Box>

        {appointment.status === 'CANCELLED' && appointment.cancellationReason && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Ce rendez-vous a ete annule. Motif: {appointment.cancellationReason}
          </Alert>
        )}

        {appointment.visit && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Une visite est deja associee a ce rendez-vous. Statut actuel: {getStatusLabel(appointment.visit.status)}
          </Alert>
        )}

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <PersonOutlineIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Visiteur
                </Typography>
              </Box>
              <Typography variant="body1">
                {appointment.firstName} {appointment.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {appointment.email}
              </Typography>
              {appointment.phoneNumber && (
                <Typography variant="body2" color="text.secondary">
                  Tel: {appointment.phoneNumber}
                </Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <EventAvailableIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Date et horaire
                </Typography>
              </Box>
              <Typography variant="body1">
                {format(new Date(appointment.scheduledAt), "EEEE d MMMM yyyy 'a' HH:mm", { locale: fr })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Arrivez quelques minutes avant l&apos;heure prevue a l&apos;accueil.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DescriptionOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Objet du rendez-vous
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {appointment.subject}
            </Typography>
            {appointment.description && (
              <Typography variant="body2" color="text.secondary">
                {appointment.description}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BusinessIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Departement
              </Typography>
            </Box>
            <Typography variant="body1">{appointment.department?.name || '-'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Hote
            </Typography>
            <Typography variant="body1">{hostName || 'Non assigne'}</Typography>
          </Grid>

          {appointment.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {appointment.notes}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 4 }}>
          <Button component={RouterLink} to="/" variant="contained" startIcon={<HomeIcon />}>
            Retour a l&apos;accueil
          </Button>
          <Button component={RouterLink} to="/login" variant="outlined" startIcon={<LoginIcon />}>
            Se connecter
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default PublicAppointmentRegistration;
